<script lang="ts">
  import { addCredits } from '../lib/components/map/credits';
  import { onMount, onDestroy } from 'svelte';
  import { navigate } from 'svelte-routing';
  import * as maplibregl from 'maplibre-gl';
  import { api } from '../lib/api/client';
  import type { CrashDetails, Fix, LiveJoined, MapInfo } from '../lib/api/types';
  import { isAuthenticated } from '../lib/stores/auth';
  import { websocket } from '../lib/stores/websocket';
  import { age, when } from '../lib/utils/time';
  import { initials } from '../lib/utils/geo';
  import { wakeCause } from '../lib/utils/memberState';
  import { watchSight } from '../lib/utils/sight';
  import { followTheme, northUpMap, styleFor } from '../lib/components/map/theme';
  import { integer, t } from '../lib/i18n/index.ts';

  // Crash shows a crash alert to a recipient (004 FR-007): where and when, a way
  // to call the member, and live following, which starts on its own.
  let { id }: { id: string } = $props();

  let crash: CrashDetails | null = $state(null);
  let error = $state('');
  let liveState = $state('');
  // liveReason says why the phone could not be reached, when the server knows.
  let liveReason = $state('');
  let mapEl: HTMLDivElement | undefined = $state();
  let map: maplibregl.Map | null = null;
  let stopTheme: (() => void) | null = null;
  let marker: maplibregl.Marker | null = null;
  let session = '';
  let unsubscribe: (() => void) | null = null;
  let rejoin: ReturnType<typeof setInterval> | null = null;
  // centering keeps the member in the middle of the map until the viewer
  // drags it (002 FR-017).
  let centering = true;
  // A page out of sight for 2 minutes stops following, and follows again
  // when it comes back while the alert still stands (002 FR-015).
  const stopWatchingSight = watchSight(
    () => {
      unfollow();
      liveState = 'away';
    },
    () => {
      if (crash?.group_id && crash.state === 'alerted') follow(crash.group_id, crash.user_id);
    },
  );

  $effect(() => {
    if (!$isAuthenticated) navigate('/login?next=/crash/' + id, { replace: true });
  });

  onMount(async () => {
    try {
      crash = await api.get<CrashDetails>(`/crashes/${id}`);
    } catch (e: any) {
      error = e.message;
      return;
    }
    const info = await api.get<MapInfo>('/map').catch(() => null);
    if (!mapEl || !crash) return;
    map = northUpMap({
      container: mapEl,
      style: styleFor(info),
      center: [crash.lon, crash.lat],
      zoom: 15,
      attributionControl: false,
    });
    // The fallback background has no sources to carry the attribution.
    addCredits(map, info, 'other');
    const el = document.createElement('div');
    el.className = 'crash-marker';
    el.textContent = initials(crash.display_name);
    marker = new maplibregl.Marker({ element: el }).setLngLat([crash.lon, crash.lat]).addTo(map);
    stopTheme = followTheme(map, info, () => {});
    map.on('dragstart', (e) => {
      if (e.originalEvent) centering = false;
    });
    if (crash.group_id && crash.state === 'alerted') follow(crash.group_id, crash.user_id);
  });

  onDestroy(() => {
    stopTheme?.();
    stopWatchingSight();
    unfollow();
    map?.remove();
  });

  // unfollow leaves the live session.
  // generation counts unfollows, so a join still waiting on the server when
  // following stops does not start it again.
  let generation = 0;

  function unfollow(): Promise<void> {
    generation++;
    unsubscribe?.();
    unsubscribe = null;
    if (rejoin) clearInterval(rejoin);
    rejoin = null;
    const left = session ? api.delete(`/live/${session}/viewers/me`).then(() => {}, () => {}) : Promise.resolve();
    session = '';
    return left;
  }

  async function follow(groupId: string, userId: string) {
    try {
      await join(groupId, userId);
      rejoin = setInterval(() => join(groupId, userId).catch(() => {}), 60_000);
    } catch {
      liveState = '';
    }
  }

  // join joins the member's live session and subscribes to it. It runs again
  // every minute and after the socket reconnects: a reconnect counts as
  // leaving, so the socket's own subscription is refused until the viewer
  // has joined again (002 FR-014, 002 FR-015).
  async function join(groupId: string, userId: string) {
    const gen = generation;
    const j = await api.post<LiveJoined>(`/groups/${groupId}/members/${userId}/live`);
    if (gen !== generation) return;
    if (!liveState || j.session_id !== session) {
      liveState = j.state;
      liveReason = j.reason ?? '';
    }
    session = j.session_id;
    unsubscribe?.();
    unsubscribe = websocket.subscribeTopic(j.topic, (payload, _t, type) => {
      if (type === 'point') {
        const f = payload as Fix;
        marker?.setLngLat([f.lon, f.lat]);
        if (centering) map?.easeTo({ center: [f.lon, f.lat], duration: 500 });
        liveState = 'streaming';
      } else if (type === 'state') {
        const st = payload as { state: string; reason?: string };
        liveState = st.state;
        liveReason = st.reason ?? '';
        // An ended session, as after "I'm OK", is not joined again, which
        // would wake the member's phone; the crash is read afresh instead.
        if (st.state === 'ended') {
          unfollow();
          liveState = 'ended';
          api.get<CrashDetails>(`/crashes/${id}`).then((c) => (crash = c)).catch(() => {});
        }
      }
    });
  }

  let wasConnected = false;
  $effect(() => {
    const connected = $websocket.connected;
    if (connected && !wasConnected && session && crash?.group_id) join(crash.group_id, crash.user_id).catch(() => {});
    wasConnected = connected;
  });

  // tryAgain leaves and joins again, which wakes the phone again, as the
  // map's Follow again does (spec 012).
  async function tryAgain() {
    if (!crash?.group_id) return;
    // Leaving must reach the server before joining again, or the join finds
    // the viewer still in and wakes nothing.
    await unfollow();
    follow(crash.group_id, crash.user_id);
  }

  // liveWords says how following goes; a state the page does not know shows
  // as the server named it.
  function liveWords(state: string, reason: string): string {
    switch (state) {
      case 'waiting': return t('crashLiveWaiting');
      case 'streaming': return t('crashLiveStreaming');
      case 'unreachable': return reason ? t('crashLiveUnreachableBecause', { cause: wakeCause(reason) }) : t('crashLiveUnreachable');
      case 'ended': return t('crashLiveEnded');
      case 'away': return t('crashLiveAway');
      default: return state;
    }
  }
</script>

<div class="flex flex-1 min-h-0">
  <aside class="w-96 shrink-0 border-r p-5 space-y-3" style="border-color: var(--theme-surface0); background: var(--theme-mantle);">
    {#if error}
      <p style="color: var(--theme-error);">{error}</p>
    {:else if crash}
      <h1 class="text-xl font-semibold">
        {t('crashTitle', { state: crash.state, member: crash.display_name })}
      </h1>
      <p class="text-sm">{t('crashDetected', { when: when(crash.detected_at), age: age(crash.detected_at), accuracy: integer(Math.round(crash.accuracy_m)) })}</p>
      {#if crash.phone}
        <a class="btn btn-primary inline-block" href={`tel:${crash.phone}`}><i aria-hidden="true" class="las la-phone mr-1"></i>{t('crashCall', { member: crash.display_name, phone: crash.phone })}</a>
      {:else}
        <p class="text-sm text-text-muted">{t('crashNoPhone', { member: crash.display_name })}</p>
      {/if}
      {#if liveState}
        <p class="text-sm text-text-muted">
          <i aria-hidden="true" class="las la-broadcast-tower mr-1"></i>{liveWords(liveState, liveReason)}
        </p>
        {#if liveState === 'unreachable'}
          <button class="btn btn-secondary btn-sm" onclick={tryAgain}>{t('appRetry')}</button>
        {/if}
      {/if}
    {:else}
      <p class="text-text-muted">{t('crashLoading')}</p>
    {/if}
  </aside>
  <div class="flex-1 relative min-w-0">
    <div bind:this={mapEl} class="absolute inset-0"></div>
  </div>
</div>

<style>
  :global(.crash-marker) {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.8rem;
    background: var(--theme-error);
    color: var(--theme-base);
    border: 3px solid var(--theme-base);
    box-shadow: var(--theme-shadow-md);
  }
</style>
